<template>
  <div v-loading="loading" class="page-container dashboard">
    <div class="dashboard-toolbar">
      <div>
        <strong>{{ scopeText }}</strong>
        <small v-if="dashboard">本周 {{ dashboard.week_start }} 至 {{ dashboard.week_end }} · 更新于 {{ dashboard.generated_at }}</small>
      </div>
      <el-button size="small" icon="el-icon-refresh" :disabled="loading" @click="load">刷新数据</el-button>
    </div>
    <el-alert v-if="loadError" :title="loadError" type="error" :closable="false" show-icon class="load-error" />
    <template v-if="dashboard">
      <div v-if="!dashboard.regions.length" class="scope-empty">当前账号没有可展示的启用省区，请联系管理员维护省区管理关系。</div>
      <div class="stats">
        <div v-for="item in dashboard.stats" :key="item.label" class="stat panel"><span>{{ item.label }}</span><strong>{{ item.value }}</strong><small>{{ item.hint }}</small></div>
      </div>
      <div class="grid">
        <div class="panel chart-panel">
          <div class="panel-title">近 30 日行为趋势 <small>拜访次数 · 科室会场次 · 培训完成人次</small></div>
          <div v-if="!hasTrend" class="trend-empty">近 30 日暂无行为记录</div>
          <div ref="chart" class="chart"></div>
        </div>
        <div class="panel todos">
          <div class="panel-title">待办提醒</div>
          <div v-for="todo in dashboard.todos" :key="todo.code" class="todo"><span :class="todo.level"></span><div>{{ todo.text }}<small>{{ todo.hint }}</small></div><b>{{ todo.count }}</b></div>
          <div v-if="!dashboard.todos.length" class="empty-state">当前数据范围内暂无待办提醒</div>
        </div>
      </div>
      <div class="panel health">
        <div class="panel-title">本月行为健康度 <small>{{ dashboard.month }} · {{ healthNote }}</small></div>
        <el-table :data="dashboard.health" empty-text="本月暂无已计算的考核结果，请在考核看板计算本月批次">
          <el-table-column prop="name" label="考核维度 / 指标" min-width="280" />
          <el-table-column prop="actual" label="团队完成值" />
          <el-table-column prop="target" label="目标 / 应完成量" />
          <el-table-column prop="rate" label="完成率" />
          <el-table-column label="状态"><template slot-scope="{row}"><span :class="'health-status ' + row.level">{{ row.state }}</span></template></el-table-column>
        </el-table>
      </div>
    </template>
  </div>
</template>
<script>
import * as echarts from 'echarts'
import { moduleApi } from '../services/api'

export default {
  data: () => ({ dashboard: null, loading: false, loadError: '', requestSeq: 0 }),
  computed: {
    scopeText () {
      if (!this.dashboard) return this.loadError ? '当前账号的数据范围加载失败' : '正在获取当前账号的数据范围'
      const names = this.dashboard.regions.join('、')
      return this.dashboard.scope_label + (names ? '：' + names : '：暂无省区')
    },
    hasTrend () { return this.dashboard && this.dashboard.trend.some(point => point.visits || point.meetings || point.training) },
    healthNote () {
      return this.dashboard.health_calculated_at ? '考核结果最近计算于 ' + this.dashboard.health_calculated_at : '读取本月考核结果'
    }
  },
  mounted () {
    this.load()
    window.addEventListener('resize', this.resizeChart)
    window.addEventListener('focus', this.refreshVisible)
    this.refreshTimer = setInterval(this.refreshVisible, 60000)
  },
  beforeDestroy () {
    this.requestSeq++
    clearInterval(this.refreshTimer)
    window.removeEventListener('resize', this.resizeChart)
    window.removeEventListener('focus', this.refreshVisible)
    this.disposeChart()
  },
  methods: {
    async load () {
      if (this.loading) return
      const sequence = ++this.requestSeq
      this.loading = true
      this.loadError = ''
      try {
        const value = await moduleApi.dashboard()
        if (sequence !== this.requestSeq) return
        this.dashboard = value
        await this.$nextTick()
        if (sequence === this.requestSeq) this.renderChart()
      } catch (error) {
        if (sequence !== this.requestSeq) return
        this.dashboard = null
        this.disposeChart()
        if (!error.authExpired) this.loadError = '工作台数据加载失败：' + (error.message || '请刷新重试')
      } finally {
        if (sequence === this.requestSeq) this.loading = false
      }
    },
    refreshVisible () { if (!document.hidden) this.load() },
    resizeChart () { if (this.chart) this.chart.resize() },
    disposeChart () { if (this.chart) { this.chart.dispose(); this.chart = null } },
    renderChart () {
      if (!this.$refs.chart || !this.dashboard) return
      if (!this.chart) this.chart = echarts.init(this.$refs.chart)
      const points = this.dashboard.trend
      this.chart.setOption({
        tooltip: { trigger: 'axis' }, legend: { data: ['拜访', '科室会', '培训完成'] },
        grid: { left: 45, right: 20, top: 40, bottom: 35 },
        xAxis: { type: 'category', data: points.map(point => point.date.slice(5)) },
        yAxis: { type: 'value', minInterval: 1 },
        series: [
          { name: '拜访', type: 'bar', data: points.map(point => point.visits), itemStyle: { color: '#108ccf' } },
          { name: '科室会', type: 'line', data: points.map(point => point.meetings), itemStyle: { color: '#e8a13a' } },
          { name: '培训完成', type: 'line', data: points.map(point => point.training), itemStyle: { color: '#2e9e5b' } }
        ]
      }, true)
      this.chart.resize()
    }
  }
}
</script>
<style scoped>
.dashboard-toolbar{display:flex;align-items:center;justify-content:space-between;gap:18px;margin-bottom:16px}.dashboard-toolbar strong{font-size:13px;color:#536575;line-height:1.6}.dashboard-toolbar small{display:block;color:#8a98a3;margin-top:5px;font-size:11px}.load-error,.scope-empty{margin-bottom:16px}.scope-empty{padding:12px 16px;border-radius:8px;background:#fff7e6;color:#976819;font-size:13px}.stats{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:14px;margin-bottom:16px}.stat{padding:18px}.stat span,.stat small{display:block;color:#71818d}.stat strong{display:block;font-size:29px;margin:10px 0;color:#0b7fb8}.stat small{font-size:11px;line-height:1.6}.grid{display:grid;grid-template-columns:1.45fr 1fr;gap:16px;margin-bottom:16px}.chart{height:330px}.chart-panel{position:relative}.panel-title small{font-weight:400;font-size:11px;color:#8a98a3;margin-left:8px}.trend-empty{position:absolute;top:53%;width:100%;text-align:center;color:#8a98a3;pointer-events:none;z-index:1}.empty-state{padding:50px 20px;text-align:center;color:#8a98a3;font-size:13px}.todo{display:flex;align-items:center;gap:12px;padding:17px 20px;border-bottom:1px solid #edf1f4}.todo>span{width:9px;height:9px;flex:none;border-radius:50%}.todo .red{background:#d74444}.todo .orange{background:#d58a22}.todo .green{background:#218f55}.todo div{flex:1}.todo small{display:block;color:#8a98a3;margin-top:5px;font-size:11px;line-height:1.5}.health{overflow:hidden}.health-status{display:inline-block;padding:3px 9px;border-radius:8px;font-size:12px}.health-status.pass{background:#e8f7ee;color:#218f55}.health-status.wait{background:#fff4df;color:#976819}.health-status.unknown{background:#f3f4f6;color:#71818d}@media(max-width:1100px){.stats{grid-template-columns:repeat(3,minmax(0,1fr))}.grid{grid-template-columns:1fr}.dashboard-toolbar{align-items:flex-start}}@media(max-width:700px){.stats{grid-template-columns:repeat(2,minmax(0,1fr))}.panel-title small{display:block;margin:6px 0 0}}
</style>
